import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ProjectAccountDTO,
  CustomDocumentDTO,
  BookkeepingDTO,
} from "../../models/ProjectAccount.model";
import { getProjectAccount } from "../thunks/projectAccountThunks";

interface ProjectAccountState {
  projectAccount: ProjectAccountDTO | null;
  loading: boolean;
  error: string | null;

  // מסמך נבחר לצפייה
  selectedDocumentForViewing: CustomDocumentDTO | null;
  selectedBookkeepingForViewing: BookkeepingDTO | null;
  pdfViewerMode: "document" | "bookkeeping" | null;
}

const initialState: ProjectAccountState = {
  projectAccount: null,
  loading: false,
  error: null,
  selectedDocumentForViewing: null,
  selectedBookkeepingForViewing: null,
  pdfViewerMode: null,
};

const projectAccountSlice = createSlice({
  name: "projectAccount",
  initialState,
  reducers: {
    clearProjectAccountError(state) {
      state.error = null;
    },
    clearProjectAccount(state) {
      state.projectAccount = null;
    },

    // פעולות עבור מסמך נבחר
    setSelectedDocumentForViewing: (
      state,
      action: PayloadAction<CustomDocumentDTO>,
    ) => {
      state.selectedDocumentForViewing = action.payload;
      state.selectedBookkeepingForViewing = null;
      state.pdfViewerMode = "document";
    },

    setSelectedBookkeepingForViewing: (
      state,
      action: PayloadAction<BookkeepingDTO>,
    ) => {
      state.selectedBookkeepingForViewing = action.payload;
      state.selectedDocumentForViewing = null;
      state.pdfViewerMode = "bookkeeping";
    },

    clearSelectedDocumentForViewing: (state) => {
      state.selectedDocumentForViewing = null;
      state.selectedBookkeepingForViewing = null;
      state.pdfViewerMode = null;
    },

    // 🆕 עדכון סטטוס מסמך נבחר מקומית
    updateSelectedDocumentStatus: (state, action: PayloadAction<number>) => {
      if (state.selectedDocumentForViewing) {
        state.selectedDocumentForViewing.projectDocument_StatusCode =
          action.payload;
        state.selectedDocumentForViewing.projectDocument_Status = getActionText(
          action.payload,
        );
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProjectAccount.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProjectAccount.fulfilled, (state, action) => {
        state.loading = false;
        state.projectAccount = action.payload.projectAccount;
      })
      .addCase(getProjectAccount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "שגיאה בקבלת נתוני חשבונות הפרויקט";
      });
  },
});

export const {
  clearProjectAccountError,
  clearProjectAccount,
  setSelectedDocumentForViewing,
  setSelectedBookkeepingForViewing,
  clearSelectedDocumentForViewing,
  updateSelectedDocumentStatus, // 🆕
} = projectAccountSlice.actions;

// Selectors
export const selectSelectedDocumentForViewing = (state: {
  projectAccount: ProjectAccountState;
}) => state.projectAccount.selectedDocumentForViewing;

export const selectSelectedBookkeepingForViewing = (state: {
  projectAccount: ProjectAccountState;
}) => state.projectAccount.selectedBookkeepingForViewing;

export const selectPdfViewerMode = (state: {
  projectAccount: ProjectAccountState;
}) => state.projectAccount.pdfViewerMode;

export default projectAccountSlice.reducer;
function getActionText(payload: number): string {
  switch (payload) {
    case 0:
      return "מבוטל";
    case 1:
      return "נוצר";
    case 2:
      return "חתום";
    default:
      return "לא ידוע";
  }
}
